import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { cn } from "../lib/utils"
import { useSidebar } from "../hooks/use-layout"
import { ScrollArea } from "../components/ui/scroll-area"
import { publicAssetPath } from "helpers/publicAssetPath"
import { getAppVersionLabel, getAppVersionShortLabel } from "helpers/appVersion"
import { collectSidebarLinks, resolveActiveNavLink } from "helpers/navActiveLink"
import { filterSidebarMenu } from "helpers/filterSidebarMenu"
import { resolveSidebarIcon } from "helpers/resolveSidebarIcon"
import {
 Tooltip,
 TooltipContent,
 TooltipProvider,
 TooltipTrigger,
} from "../components/ui/tooltip"
import {
 ChevronLeft,
 ChevronRight,
 Search,
 SearchX,
 X,
} from "lucide-react"

interface NavItem {
 label: string
 id?: string
 icon?: string
 link?: string
 subItems?: NavItem[]
 perm?: string | string[]
 type?:"section"
 preload?: () => void
}

interface SidebarProps {
 menuItems: NavItem[]
 logo?: string
 appName?: string
 collapsed?: boolean
 inDrawer?: boolean
}

export function Sidebar({ menuItems, appName, inDrawer }: SidebarProps) {
 const { i18n, t } = useTranslation()
 const isRtl = i18n.dir() === "rtl"
 const { isCollapsed, closeMobile, expand, toggle } = useSidebar()
 const effectiveCollapsed = inDrawer ? false : isCollapsed
 const location = useLocation()
 const navigate = useNavigate()
 const fallbackLogo = publicAssetPath("logo.png")
 const [fallbackLogoFailed, setFallbackLogoFailed] = useState(false)
 const showLogo = Boolean(fallbackLogo) && !fallbackLogoFailed
 const [searchValue, setSearchValue] = useState("")
 const searchInputRef = useRef<HTMLInputElement>(null)
 const navigationRef = useRef<HTMLElement>(null)
 const isSearching = Boolean(searchValue.trim())
 const filteredMenuItems = useMemo(
 () => {
  const items = filterSidebarMenu(menuItems, searchValue)
  const intelligenceIndex = items.findIndex((item) => item.id === 'nav-intelligence')
  if (intelligenceIndex <= 0) return items

  return [
   items[intelligenceIndex],
   ...items.slice(0, intelligenceIndex),
   ...items.slice(intelligenceIndex + 1),
  ]
 },
 [menuItems, searchValue],
 )

 const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.defaultPrevented || !isSearching || event.nativeEvent.isComposing) return

  if (event.key === 'Escape') {
   event.preventDefault()
   setSearchValue('')
   return
  }

  if (event.key === 'Enter') {
   const firstLink = navigationRef.current?.querySelector<HTMLAnchorElement>('a[href]')
   const destination = firstLink?.getAttribute('href')
   if (destination) {
    event.preventDefault()
    navigate(destination)
   }
   return
  }

  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
   const links = navigationRef.current?.querySelectorAll<HTMLAnchorElement>('a[href]')
   const target = event.key === 'ArrowDown' ? links?.[0] : links?.[links.length - 1]
   if (target) {
    event.preventDefault()
    target.focus()
   }
  }
 }, [isSearching, navigate])

 const allNavLinks = useMemo(() => collectSidebarLinks(menuItems), [menuItems])
 const activeNavLink = useMemo(
  () => resolveActiveNavLink(allNavLinks, location.pathname, location.search),
  [allNavLinks, location.pathname, location.search],
 )
 const appVersionLabel = getAppVersionLabel()
 const appVersionShortLabel = getAppVersionShortLabel()

 const isActive = useCallback(
 (link: string) => activeNavLink === link,
 [activeNavLink],
 )

 const hasActiveChild = useCallback((item: NavItem): boolean => {
 if (!item.subItems || !activeNavLink) return false
 return item.subItems.some((sub) => sub.link === activeNavLink)
 }, [activeNavLink])

 const getMenuKey = useCallback((item: NavItem, index: number) => {
 if (item.type === "section") return `section:${item.label}:${index}`
 return item.id || item.link || `item:${item.label}:${index}`
 }, [])

 const getDefaultOpenMenus = useCallback((): Record<string, boolean> => {
 const open: Record<string, boolean> = {}
 menuItems.forEach((item, index) => {
 if (item.subItems && hasActiveChild(item)) {
 open[getMenuKey(item, index)] = true
 }
 })
 return open
 }, [getMenuKey, hasActiveChild, menuItems])

 const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => getDefaultOpenMenus())

 useEffect(() => {
 setOpenMenus((prev) => {
 const next = { ...prev }
 let changed = false

 menuItems.forEach((item, index) => {
 if (item.subItems && hasActiveChild(item)) {
 const key = getMenuKey(item, index)
 if (!next[key]) {
 next[key] = true
 changed = true
 }
 }
 })

 return changed ? next : prev
 })
 }, [getMenuKey, hasActiveChild, menuItems])

 const toggleMenu = (id: string) => {
 setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }))
 }

 const prevPathnameRef = useRef(location.pathname)
 useEffect(() => {
 if (prevPathnameRef.current !== location.pathname) {
 prevPathnameRef.current = location.pathname
 closeMobile()
 }
 }, [location.pathname, closeMobile])

 useEffect(() => {
 setFallbackLogoFailed(false)
 }, [fallbackLogo])

 const focusSidebarSearch = useCallback(() => {
 expand()
 window.setTimeout(() => searchInputRef.current?.focus(), 0)
 }, [expand])

 const sidebarDir = isRtl ? "rtl" : "ltr"
 const toggleLabel = isCollapsed
 ? (isRtl ? "توسيع القائمة الجانبية" : "Expand sidebar")
 : (isRtl ? "طي القائمة الجانبية" : "Collapse sidebar")

 return (
 <TooltipProvider delayDuration={0}>
 <aside
 dir={sidebarDir}
 className={cn(
 inDrawer
 ? "app-sidebar relative h-full w-full gradient-sidebar-main text-sidebar-foreground": cn(
"app-sidebar fixed top-[var(--app-banner-h,0px)] z-40 h-[calc(100vh-var(--app-banner-h,0px))] border-e border-sidebar-border/70 gradient-sidebar-main text-sidebar-foreground transition-[width,top,height] duration-200 ease-out",
"hidden md:block",
 isRtl ? "right-0":"left-0",
 isCollapsed ? "w-sidebar-collapsed":"w-sidebar")
 )}
 >
 {!inDrawer && (
 <button
 type="button"
 onClick={toggle}
 aria-label={toggleLabel}
 title={toggleLabel}
 aria-pressed={isCollapsed}
 className={cn(
 "app-sidebar-toggle",
 isRtl ? "app-sidebar-toggle--rtl" : "app-sidebar-toggle--ltr",
 )}
 >
 <span className="app-sidebar-toggle__glow" aria-hidden />
 <span className="app-sidebar-toggle__face">
 {isCollapsed
 ? (isRtl ? <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} /> : <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />)
 : (isRtl ? <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} /> : <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} />)}
 </span>
 </button>
 )}
 <div className="flex h-full min-h-0 flex-col">
 {/* ── Logo area ── */}
 <div
 className={cn(
"app-sidebar__brand flex h-14 shrink-0 items-center border-b border-white/[0.06] px-3",
 effectiveCollapsed &&"justify-center px-2")}
 >
 <Link
 to="/" className={cn(
"flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors",
"hover:bg-white/[0.08] hover:text-white",
"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
 effectiveCollapsed &&"justify-center p-1.5")}
 >
 {showLogo ? (
 <img
 src={fallbackLogo}
 alt="Logo" className={cn(
"object-contain brightness-0 invert transition-all duration-300",
" drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]",
 effectiveCollapsed ? "h-9 w-9 rounded-lg":"h-10 w-auto max-w-[148px]")}
 onError={() => setFallbackLogoFailed(true)}
 />
 ) : (
 <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white font-bold text-lg shadow-lg shadow-primary/30">
 E
 </div>
 )}
 {!effectiveCollapsed && (
 <span className="min-w-0 truncate text-[0.9375rem] font-bold leading-tight tracking-tight text-sidebar-primary">
 {appName || "Madar Pos"}
 </span>
 )}
 </Link>
	 </div>

	 {/* ── Navigation search ── */}
	 {effectiveCollapsed ? (
	 <div className="flex shrink-0 justify-center border-b border-white/[0.06] px-2 py-2">
	 <Tooltip>
	 <TooltipTrigger asChild>
	 <button
	 type="button"
	 onClick={focusSidebarSearch}
	 aria-label={t("ui.searchLabel")}
	 className="flex h-9 w-9 items-center justify-center rounded-lg text-sidebar-foreground/70 transition-colors hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
	 >
	 <Search className="h-4 w-4" aria-hidden />
	 </button>
	 </TooltipTrigger>
	 <TooltipContent side={isRtl ? "left" : "right"} className="font-medium">
	 {t("ui.searchLabel")}
	 </TooltipContent>
	 </Tooltip>
	 </div>
	 ) : (
	 <div className="shrink-0 border-b border-white/[0.06] px-3 py-2.5">
	 <div className="relative">
	 <Search
	 className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-foreground/45"
	 aria-hidden
	 />
	 <input
	 ref={searchInputRef}
	 type="search"
	 value={searchValue}
	 onChange={(event) => setSearchValue(event.target.value)}
	 placeholder={t("ui.searchPlaceholder")}
	 aria-label={t("ui.searchLabel")}
	 aria-controls="workspace-navigation-links"
	 onKeyDown={handleSearchKeyDown}
	 className="app-sidebar__search h-9 w-full rounded-lg border border-white/10 bg-black/15 ps-9 pe-9 text-sm text-sidebar-foreground outline-none transition-all placeholder:text-sidebar-foreground/45 hover:border-white/20 focus:border-white/30 focus:bg-black/25 focus:ring-2 focus:ring-white/10"
	 />
	 {isSearching && (
	 <button
	 type="button"
	 onClick={() => setSearchValue("")}
	 aria-label={t("ui.clearFiltersShort")}
	 className="absolute end-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-sidebar-foreground/55 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
	 >
	 <X className="h-3.5 w-3.5" aria-hidden />
	 </button>
	 )}
	 </div>
	 </div>
	 )}

	 {/* ── Nav ── */}
	 <ScrollArea className="min-h-0 flex-1 py-2">
	 <nav
	  id="workspace-navigation-links"
	  ref={navigationRef}
	  dir={sidebarDir}
	  className="flex flex-col gap-1 px-2 pb-3"
	 >
	 {filteredMenuItems.map((item, index) => {
	 const menuKey = getMenuKey(item, index)
 return (
 <SidebarItem
 key={menuKey}
 item={item}
 isCollapsed={effectiveCollapsed}
 isRtl={isRtl}
 isActive={item.link ? isActive(item.link) : hasActiveChild(item)}
 activeNavLink={activeNavLink}
	 isOpen={isSearching || openMenus[menuKey] || false}
	 onToggle={() => toggleMenu(menuKey)}
	 />
	 )
	 })}
	 {isSearching && filteredMenuItems.length === 0 && (
	 <div className="flex flex-col items-center gap-2 px-3 py-10 text-center text-sidebar-foreground/55">
	 <SearchX className="h-5 w-5" aria-hidden />
	 <p className="text-sm font-medium">{t("ui.commandPalette.noResults")}</p>
	 </div>
	 )}
	 </nav>
 </ScrollArea>

 {/* ── Footer ── */}
 <div
 className={cn(
"border-t border-white/10 px-3 py-2.5",
 effectiveCollapsed &&"flex justify-center px-2 py-2")}
 >
 {!effectiveCollapsed ? (
 <div className="flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5">
 <span className="h-1 w-1 shrink-0 rounded-full bg-white/35"aria-hidden />
     <span className="text-[11px] font-medium text-white/50 tabular-nums">
      {appVersionLabel}
     </span>
 </div>
 ) : (
 <Tooltip>
 <TooltipTrigger asChild>
 <div
      className="flex h-8 w-8 cursor-default items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-[10px] font-semibold text-white/50 tabular-nums" aria-label={appVersionLabel}>
      {appVersionShortLabel}
 </div>
 </TooltipTrigger>
     <TooltipContent side={isRtl ? "left" : "right"} className="font-medium">
      {appVersionLabel}
     </TooltipContent>
 </Tooltip>
 )}
 </div>
 </div>
 </aside>
 </TooltipProvider>
 )
}

interface SidebarItemProps {
 item: NavItem
 isCollapsed: boolean
 isRtl: boolean
 isActive: boolean
 activeNavLink: string | null
 isOpen: boolean
 onToggle: () => void
}

function SidebarItem({ item, isCollapsed, isRtl, isActive, activeNavLink, isOpen, onToggle }: SidebarItemProps) {
 const IconComponent = item.icon ? resolveSidebarIcon(item.icon) : null

 const isSubActive = (link: string) => activeNavLink === link

 if (item.type === "section") {
 return isCollapsed ? null : (
 <div className="px-1 pt-4 pb-1 first:pt-2">
 <div className="flex items-center gap-2 px-2">
 <span className="h-px min-w-[1rem] flex-1 bg-white/10" aria-hidden />
 <span className="shrink-0 text-[11px] font-semibold leading-none text-white/55">
 {item.label}
 </span>
 <span className="h-px min-w-[1rem] flex-1 bg-white/10" aria-hidden />
 </div>
 </div>
 )
 }

 if (item.subItems) {
 return (
 <div>
 {item.link ? (
 <div className="relative">
 <SidebarItemWrapper isCollapsed={isCollapsed} isRtl={isRtl} tooltip={item.label}>
 <Link
 to={item.link}
 onMouseEnter={() => item.preload?.()}
 className={cn(
"app-sidebar__nav-item flex w-full min-h-[2.5rem] items-center gap-3 rounded-xl border-s-2 border-s-transparent px-3 py-2 text-sm font-medium transition-all duration-200",
 !isCollapsed && "pe-10",
"hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground",
 isActive
 ? "app-sidebar__nav-item--active border-s-[hsl(var(--brand-accent))] bg-[hsl(var(--brand-accent)_/_0.14)] font-semibold text-sidebar-accent-foreground": isOpen
 ? "app-sidebar__nav-item--open bg-sidebar-accent/45 text-sidebar-foreground":"text-sidebar-foreground/85")}
 >
 {IconComponent && (
 <IconComponent
 className={cn(
"h-5 w-5 shrink-0 transition-colors",
 isActive
 ? "text-[hsl(var(--brand-accent))]":"text-sidebar-foreground/50")}
 />
 )}
 {!isCollapsed && <span className="min-w-0 flex-1 truncate text-start">{item.label}</span>}
 </Link>
 </SidebarItemWrapper>
 {!isCollapsed && (
 <button
 type="button"
 onClick={onToggle}
 aria-label={item.label}
 aria-expanded={isOpen}
 title={item.label}
 className="absolute inset-y-0 end-1 z-10 inline-flex w-8 items-center justify-center rounded-lg text-sidebar-foreground/45 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
 >
 <ChevronLeft
 className={cn(
"h-4 w-4 shrink-0 transition-transform duration-200",
 isRtl && !isOpen && "rotate-180",
 isOpen && (isRtl ? "rotate-90" : "-rotate-90"))}
 />
 </button>
 )}
 </div>
 ) : (
 <SidebarItemWrapper isCollapsed={isCollapsed} isRtl={isRtl} tooltip={item.label}>
 <button
 type="button"
 onClick={onToggle}
 className={cn(
"app-sidebar__nav-item flex w-full min-h-[2.5rem] items-center gap-3 rounded-xl border-s-2 border-s-transparent px-3 py-2 text-sm font-medium transition-all duration-200",
"hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground",
 isActive
 ? "app-sidebar__nav-item--active border-s-[hsl(var(--brand-accent))] bg-[hsl(var(--brand-accent)_/_0.14)] font-semibold text-sidebar-accent-foreground": isOpen
 ? "app-sidebar__nav-item--open bg-sidebar-accent/45 text-sidebar-foreground":"text-sidebar-foreground/85")}
 >
 {IconComponent && (
 <IconComponent
 className={cn(
"h-5 w-5 shrink-0 transition-colors",
 isActive
 ? "text-[hsl(var(--brand-accent))]":"text-sidebar-foreground/50")}
 />
 )}
 {!isCollapsed && (
 <>
 <span className="min-w-0 flex-1 truncate text-start">{item.label}</span>
 <ChevronLeft
 className={cn(
"h-4 w-4 shrink-0 text-sidebar-foreground/45 transition-transform duration-200",
 isRtl && !isOpen && "rotate-180",
 isOpen && (isRtl ? "rotate-90" : "-rotate-90"))}
 />
 </>
 )}
 </button>
 </SidebarItemWrapper>
 )}
 {!isCollapsed && isOpen && (
 <div className="app-sidebar__subnav ms-2 mt-1 space-y-0.5 rounded-xl py-1.5 ps-2 pe-1">
 {item.subItems.map((sub, idx) => {
  if (sub.type === 'section') {
   return (
    <div
     key={`section-${sub.label}-${idx}`}
     className="px-2.5 pt-2 pb-0.5 text-[11px] font-semibold leading-snug text-sidebar-foreground/50 first:pt-1"
    >
     {sub.label}
    </div>
   )
  }
  return (
 <Link
 key={sub.link || idx}
 to={sub.link || "/"}
 className={cn(
"app-sidebar__subitem flex min-h-[2.25rem] items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] leading-snug transition-all duration-200",
 sub.link && isSubActive(sub.link)
 ? "app-sidebar__subitem--active bg-[hsl(var(--brand-accent)_/_0.16)] font-semibold text-white":"text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground")}
 >
 <span
 className={cn(
"me-0.5 h-1.5 w-1.5 shrink-0 rounded-full transition-colors",
 sub.link && isSubActive(sub.link)
 ? "bg-[hsl(var(--brand-accent))]":"bg-sidebar-foreground/25")}
 />
 <span className="min-w-0 flex-1 truncate">{sub.label}</span>
 </Link>
  )
 })}
 </div>
 )}
 </div>
 )
 }

 return (
 <SidebarItemWrapper isCollapsed={isCollapsed} isRtl={isRtl} tooltip={item.label}>
 <Link
 to={item.link || "/"}
 onMouseEnter={() => item.preload?.()}
 className={cn(
"app-sidebar__nav-item flex min-h-[2.5rem] items-center gap-3 rounded-xl border-s-2 border-s-transparent px-3 py-2 text-sm font-medium transition-all duration-200",
 isActive
 ? "app-sidebar__nav-item--active border-s-[hsl(var(--brand-accent))] bg-[hsl(var(--brand-accent)_/_0.14)] font-semibold text-sidebar-accent-foreground":"text-sidebar-foreground/85 hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground")}
 >
 {IconComponent && (
 <IconComponent
 className={cn(
"h-5 w-5 shrink-0 transition-colors",
 isActive
 ? "text-[hsl(var(--brand-accent))]":"text-sidebar-foreground/50")}
 />
 )}
 {!isCollapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
 </Link>
 </SidebarItemWrapper>
 )
}

function SidebarItemWrapper({
 isCollapsed,
 isRtl,
 tooltip,
 children,
}: {
 isCollapsed: boolean
 isRtl: boolean
 tooltip: string
 children: React.ReactNode
}) {
 if (isCollapsed) {
 return (
 <Tooltip>
 <TooltipTrigger asChild>{children}</TooltipTrigger>
 <TooltipContent side={isRtl ? "left" : "right"} className="max-w-[220px] font-medium">
 {tooltip}
 </TooltipContent>
 </Tooltip>
 )
 }
 return <>{children}</>
}
